export interface ApiIssue {
  path: string;
  message: string;
  code?: string;
}

/** RFC 9457 problem details from the REST API, as a typed error. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly title: string;
  readonly detail?: string;
  readonly errors: ApiIssue[];
  readonly retryAfter?: number;

  constructor(init: {
    status: number;
    code: string;
    title: string;
    detail?: string;
    errors?: ApiIssue[];
    retryAfter?: number;
  }) {
    super(init.detail ?? init.title);
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.title = init.title;
    if (init.detail !== undefined) this.detail = init.detail;
    this.errors = init.errors ?? [];
    if (init.retryAfter !== undefined) this.retryAfter = init.retryAfter;
  }

  /** Field → message map for forms. */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.errors) if (!(e.path in out)) out[e.path] = e.message;
    return out;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/** Build an ApiError from a failed Response (problem+json or anything else). */
export async function toApiError(res: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // not JSON
  }
  const retryHeader = res.headers.get("retry-after");
  const retryAfter = retryHeader && /^\d+$/.test(retryHeader) ? Number(retryHeader) : undefined;
  const b = isRecord(body) ? body : {};
  const errors = Array.isArray(b.errors)
    ? b.errors.filter(isRecord).map((e) => ({
        path: String(e.path ?? ""),
        message: String(e.message ?? ""),
        ...(e.code ? { code: String(e.code) } : {}),
      }))
    : [];
  return new ApiError({
    status: res.status,
    code: typeof b.code === "string" ? b.code : `HTTP_${res.status}`,
    title: typeof b.title === "string" ? b.title : res.statusText || `HTTP ${res.status}`,
    ...(typeof b.detail === "string" ? { detail: b.detail } : {}),
    errors,
    ...(retryAfter !== undefined ? { retryAfter } : {}),
  });
}
